import React, { useState, useEffect, useCallback } from 'react';
import { useClientFeatures } from '@frontend/context/ClientFeatureContext';
import { CatalogSelectionRuleConditionOperationType } from '@frontend/applications/ProductFulfillmentApp/types/globalTypes';
import {
  PlusIcon,
  MinusIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  ArrowUpRightIcon,
  ChevronLeftIcon,
  ChevronRightIcon as ChevronRightNavIcon,
  MagnifyingGlassIcon
} from '@revfluence/fresh-icons/regular/esm';
import { PFAFreeProductBoostInput } from '@frontend/applications/AffiliatesApp/types/globalTypes';
import { Dialog, DialogTitle, DialogHeader, DialogContent, DialogTrigger, DialogFooter } from '@/shadcn/components/ui/dialog';
import { Button } from '@/shadcn/components/ui/button';
import { Input } from '@/shadcn/components/ui/input';
import { Checkbox } from '@/shadcn/components/ui/checkbox';
import { Badge } from '@/shadcn/components/ui/badge';
import { Label } from '@/shadcn/components/ui/label';
import { cn } from '@/shadcn/lib/utils';
import { useGetProductsFromShopify } from '@/app/containers/Settings/ProductFulfillment/Products/hooks/useGetProductsFromShopify';

export interface GiftProductsModalProps {
  setSelectedGiftVariants: (selectedGiftVariants: PFAFreeProductBoostInput[]) => void;
  selectedGiftVariants: PFAFreeProductBoostInput[];
}

const PAGE_SIZE = 50;

export const GiftProductsModal: React.FC<GiftProductsModalProps> = ({ setSelectedGiftVariants, selectedGiftVariants }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedProducts, setExpandedProducts] = useState<Set<string>>(new Set());
  const [pageNumber, setPageNumber] = useState(1);
  const [visited, setVisited] = useState<{ [key: number]: boolean }>({});
  const [initialSearchDone, setInitialSearchDone] = useState(false);
  const [selectedVariants, setSelectedVariants] = useState<PFAFreeProductBoostInput[]>(selectedGiftVariants);
  const { bypassShopifyInventory } = useClientFeatures();

  // Shopify API hook
  const {
    fetchShopifyProducts,
    isShopifyProductsLoading,
    shopifyProductsResp,
    shopifyProductsError,
    fetchMoreShopifyProducts
  } = useGetProductsFromShopify({
    notifyOnNetworkStatusChange: true,
  });

  // Get paginated products - following ExportProductModal pattern
  const paginatedShopifyProducts = shopifyProductsResp?.shopifyProducts?.slice(
    (pageNumber - 1) * PAGE_SIZE,
    pageNumber * PAGE_SIZE,
  );

  // Pagination handlers - following ExportProductModal pattern
  const hasNextPage = pageNumber * PAGE_SIZE < (shopifyProductsResp?.totalCount || 0);
  const hasPreviousPage = pageNumber > 1;

  const handleNextPage = async () => {
    if (hasNextPage && shopifyProductsResp?.pageInfo?.endCursor) {
      if (!visited[pageNumber + 1]) {
        await fetchMoreShopifyProducts({
          variables: {
            afterCursor: shopifyProductsResp.pageInfo.endCursor,
          },
          updateQuery: (prev, { fetchMoreResult }) => {
            if (!fetchMoreResult) return prev;
            return {
              getProductsFromShopify: {
                ...fetchMoreResult.getProductsFromShopify,
                shopifyProducts: [
                  ...prev.getProductsFromShopify.shopifyProducts,
                  ...fetchMoreResult.getProductsFromShopify.shopifyProducts,
                ],
              },
            };
          },
        });
        setVisited((prev) => ({ ...prev, [pageNumber + 1]: true }));
      }
      setPageNumber(pageNumber + 1);
    }
  };

  const handlePrevPage = () => {
    if (hasPreviousPage) {
      setPageNumber(pageNumber - 1);
    }
  };

  // Handle search
  const handleSearch = useCallback(async () => {
    try {
      setPageNumber(1);
      setVisited({});
      await fetchShopifyProducts({
        variables: {
          shopifyProductsFilter: {
            query: searchTerm,
            isActiveProductOnly: true,
            operation: CatalogSelectionRuleConditionOperationType.OR,
          },
          includeVariants: true,
        },
        fetchPolicy: 'network-only',
      });
      setVisited({ 1: true });
    } catch (error) {
      console.error('Error fetching products:', error);
    }
  }, [fetchShopifyProducts, searchTerm]);

  // Handle Enter key press in search input
  const handleSearchKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  const toggleProductExpansion = (productId: string) => {
    const newExpanded = new Set(expandedProducts);
    if (newExpanded.has(productId)) {
      newExpanded.delete(productId);
    } else {
      newExpanded.add(productId);
    }
    setExpandedProducts(newExpanded);
  };
  const toggleVariantSelection = ({
    productId,
    variantId,
    productImageUrl,
    productTitle,
    variantTitle,
    productUrl
  }: {
    productId: string;
    variantId: string;
    productImageUrl: string;
    productTitle: string;
    variantTitle: string;
    productUrl: string;
  }) => {
    setSelectedVariants((prev) => {
      const existing = prev.find((v) => v.variantId === variantId);
      if (existing) {
        return prev.filter((v) => v.variantId !== variantId);
      } else {
        return [...prev, { productId, variantId, quantity: 1, productImageUrl, productTitle, variantTitle, productUrl }];
      }
    });
  };
  const updateVariantQuantity = (variantId: string, quantity: number) => {
    setSelectedVariants((prev: PFAFreeProductBoostInput[]) =>
      prev.map((v) =>
        (v.variantId === variantId
          ? { ...v, quantity: Math.max(1, quantity) }
          : v)
      )
    );
  };

  const getProductSelectedCount = (productId: string) => {
    return selectedVariants.filter((v) => v.productId === productId).length;
  };

  const isVariantSelected = (variantId: string) => {
    return selectedVariants.some((v) => v.variantId === variantId);
  };

  const getVariantQuantity = (variantId: string) => {
    return selectedVariants.find((v) => v.variantId === variantId)?.quantity || 1;
  };

  const handleSelectProducts = () => {
    setSelectedGiftVariants(selectedVariants);
    setIsOpen(false);
  };

  const handleCancel = () => {
    setIsOpen(false);
  };

  // Sync internal state with prop changes
  useEffect(() => {
    setSelectedVariants(selectedGiftVariants);
  }, [selectedGiftVariants]);

  // Modify useEffect to only trigger initial search
  useEffect(() => {
    if (isOpen && !initialSearchDone) {
      handleSearch(); // Trigger initial search with empty string
      setInitialSearchDone(true);
    } else if (!isOpen) {
      setSearchTerm('');
      // Don't clear selectedVariants here - let it sync with props instead
      setExpandedProducts(new Set());
      setPageNumber(1);
      setVisited({});
      setInitialSearchDone(false); // Reset initial search state when modal closes
    }
  }, [isOpen, handleSearch, initialSearchDone]);

  // Calculate pagination display
  const startIndex = (pageNumber - 1) * PAGE_SIZE + 1;
  const endIndex = Math.min(pageNumber * PAGE_SIZE, shopifyProductsResp?.totalCount || 0);

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>
        <Button
          variant="outline"
          className="p-4 bg-grey-1 rounded-2xl border border-input flex justify-center items-center gap-2 hover:bg-grey-2 transition-colors"
        >
          <PlusIcon className="h-3 w-3 text-grey-4" />
          <span className="text-primary text-sm font-normal">Select product to gift</span>
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-[600px] sm:h-[600px] p-0 flex flex-col">
        <DialogHeader className="px-4 py-2 border-b border-border flex-shrink-0">
          <DialogTitle className="justify-start m-0 text-gray-800 text-l font-medium font-['Inter'] leading-loose">
            Select Boost Product
          </DialogTitle>
        </DialogHeader>

        {/* Search Section - Fixed position with 12px spacing */}
        <div className="px-4 pb-3 border-b border-border flex-shrink-0">
          <div className="flex gap-2">
            <div className="relative flex-1">
              <Input
                placeholder="Search products..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onKeyPress={handleSearchKeyPress}
                className="w-full"
                disabled={isShopifyProductsLoading}
              />
            </div>
            <Button
              onClick={handleSearch}
              disabled={isShopifyProductsLoading}
              className="flex items-center gap-2 px-4"
            >
              <MagnifyingGlassIcon className="h-4 w-4" />
              Search
            </Button>
          </div>
        </div>

        {/* Product List Section - Fixed height */}
        <div className="flex-1 flex flex-col min-h-0 px-4 py-3">
          {/* Header with count and pagination - Fixed position */}
          <div className="flex items-center justify-between flex-shrink-0">
            <Label className="text-sm font-medium text-primary">
              {shopifyProductsResp?.totalCount || 0} Products
              {selectedVariants.length > 0 && (
                <span className="ml-2 text-grey-4">
                  ({selectedVariants.length} variant{selectedVariants.length !== 1 ? 's' : ''} selected)
                </span>
              )}
            </Label>
            <div className="flex items-center gap-2">
              <span className="text-xs text-primary">
                {shopifyProductsResp?.totalCount ? `${startIndex}-${endIndex} of ${shopifyProductsResp.totalCount}` : '0 of 0'}
              </span>
              <Button
                variant="ghost"
                size="sm"
                onClick={handlePrevPage}
                disabled={!hasPreviousPage || isShopifyProductsLoading}
                className="h-6 w-6 p-0"
              >
                <ChevronLeftIcon className="h-3 w-3" />
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={handleNextPage}
                disabled={!hasNextPage || isShopifyProductsLoading}
                className="h-6 w-6 p-0"
              >
                <ChevronRightNavIcon className="h-3 w-3" />
              </Button>
            </div>
          </div>

          {/* Products Container - Fixed height, scrollable */}
          <div className="flex-1 min-h-0 bg-background rounded-lg border border-input relative">
            {/* Loading Overlay */}
            {isShopifyProductsLoading && (
              <div className="absolute inset-0 bg-background/80 flex items-center justify-center z-10">
                <div className="flex items-center gap-3">
                  <div className="animate-spin h-6 w-6 border-2 border-primary border-t-transparent rounded-full" />
                  <span className="text-sm text-primary">Loading products...</span>
                </div>
              </div>
            )}

            {/* Error State */}
            {shopifyProductsError && !isShopifyProductsLoading && (
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="text-center">
                  <span className="text-sm text-destructive">Error loading products: {shopifyProductsError.message}</span>
                </div>
              </div>
            )}

            {/* No Products State */}
            {!isShopifyProductsLoading && !shopifyProductsError && (!paginatedShopifyProducts?.length) && (
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="text-center">
                  <span className="text-sm text-grey-4">No products found</span>
                </div>
              </div>
            )}

            {/* Products List - Scrollable */}
            {!isShopifyProductsLoading && !shopifyProductsError && paginatedShopifyProducts?.length && (
              <div className="h-full overflow-y-auto">
                {paginatedShopifyProducts.map((product) => (
                  <div key={product.id} className="border-b border-border last:border-b-0">
                    {/* Product Header */}
                    <div className="p-2 bg-grey-1 flex items-center gap-2">
                      <button
                        onClick={() => toggleProductExpansion(product.id)}
                        className="p-1"
                      >
                        {expandedProducts.has(product.id) ? (
                          <ChevronDownIcon className="h-3 w-3 text-grey-4" />
                        ) : (
                          <ChevronRightIcon className="h-3 w-3 text-grey-4" />
                        )}
                      </button>

                      <Checkbox
                        checked={getProductSelectedCount(product.id) > 0}
                        disabled={product.shopifyProductVariants.some((v) => v.quantity <= 0 && !bypassShopifyInventory)}
                        onCheckedChange={() => {
                          // Toggle all variants in the product
                          const productVariants = product.shopifyProductVariants || [];
                          const allSelected = productVariants.every((v) => isVariantSelected(v.id));

                          if (allSelected) {
                            setSelectedVariants((prev) =>
                              prev.filter((sv) => sv.productId !== product.id)
                            );
                          } else {
                            const newSelections = productVariants
                              .filter((v) => !isVariantSelected(v.id))
                              .map((v) => ({ productId: product.id, variantId: v.id, quantity: 1, productImageUrl: v.imageUrl || product.imgUrl, productTitle: product.title, variantTitle: v.title, productUrl: product.productUrl } as PFAFreeProductBoostInput));
                            setSelectedVariants((prev) => [...prev, ...newSelections]);
                          }
                        }}
                      />

                      <img
                        src={product.imgUrl || 'https://storage.googleapis.com/aspireiq-widgets/assets/content_image_placeholder.png'}
                        alt={product.title}
                        className="w-10 h-10 rounded-md border border-input object-cover"
                      />

                      <div className="flex-1 flex items-center justify-between">
                        <div className="flex flex-col gap-1">
                          <div className="flex items-center gap-1">
                            <span className="text-sm font-medium text-primary">{product.title}</span>
                            {product.productUrl && (
                              <a href={product.productUrl} target="_blank" rel="noopener noreferrer">
                                <ArrowUpRightIcon className="h-3 w-3 text-grey-4" />
                              </a>
                            )}
                          </div>
                          <div className="text-xs text-grey-5">
                            Variants: {product.shopifyProductVariants?.length || 0}
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          {getProductSelectedCount(product.id) > 0 && (
                            <Badge variant="secondary" className="bg-blue-2 text-primary">
                              {getProductSelectedCount(product.id)} Selected
                            </Badge>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Variants */}
                    {expandedProducts.has(product.id) && (
                      <div className="border-t border-border ml-6">
                        {product.shopifyProductVariants?.map((variant) => (
                          <div key={variant.id} className="p-2 bg-grey-1 flex items-center gap-2 border-b border-border last:border-b-0 ml-6">
                            <Checkbox
                              checked={isVariantSelected(variant.id)}
                              onCheckedChange={() => toggleVariantSelection({
                                productId: product.id,
                                variantId: variant.id,
                                productImageUrl: variant.imageUrl || product.imgUrl,
                                productTitle: product.title,
                                variantTitle: variant.title,
                                productUrl: product.productUrl
                              })}
                              disabled={variant.quantity <= 0 && !bypassShopifyInventory}
                            />

                            <img
                              src={variant.imageUrl || product.imgUrl || 'https://storage.googleapis.com/aspireiq-widgets/assets/content_image_placeholder.png'}
                              alt={variant.title}
                              className="w-10 h-10 rounded-md border border-input object-cover"
                            />

                            <div className="flex-1 flex items-center justify-between">
                              <div className="flex-1">
                                <div className="text-sm font-medium text-primary">{variant.title}</div>
                                <div className="text-xs text-grey-4">
                                  {/* {variant.title} */}
                                  {/* {product.price && (
                                    <span className="ml-2 font-medium">${product.price}</span>
                                  )} */}
                                  {variant.quantity !== undefined && (
                                    <span className="ml-2 text-grey-5">Stock: {variant.quantity}</span>
                                  )}
                                </div>
                              </div>

                              {isVariantSelected(variant.id) && (
                                <div className="flex items-center gap-2 bg-background rounded-lg border border-input p-1">
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => updateVariantQuantity(variant.id, getVariantQuantity(variant.id) - 1)}
                                    className="h-6 w-6 p-0"
                                  >
                                    <MinusIcon className="h-3 w-3" />
                                  </Button>
                                  <span className="text-sm font-medium text-primary min-w-[20px] text-center">
                                    {getVariantQuantity(variant.id)}
                                  </span>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => updateVariantQuantity(variant.id, getVariantQuantity(variant.id) + 1)}
                                    className="h-6 w-6 p-0"
                                  >
                                    <PlusIcon className="h-3 w-3" />
                                  </Button>
                                </div>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        <DialogFooter className="px-6 py-4 border-t border-border flex-shrink-0">
          <Button variant="outline" onClick={handleCancel}>
            Cancel
          </Button>
          <Button
            onClick={handleSelectProducts}
            disabled={selectedVariants.length === 0}
            className={cn(
              selectedVariants.length === 0 && 'opacity-60'
            )}
          >
            Select ({selectedVariants.length})
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
